/**
 * ActiveCampaignStatusBadge — small badge on contact cards showing ActiveCampaign subscriber status.
 *
 * Hidden (returns null) when status is null, undefined, or unrecognized.
 */

import { Badge } from '@/lib/base/badges/badges';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';

import type { ActiveCampaignSubscriberStatus } from '../types';

interface ActiveCampaignStatusBadgeProps {
  status: ActiveCampaignSubscriberStatus | null | undefined;
}

const STATUS_DISPLAY: Record<
  ActiveCampaignSubscriberStatus,
  {
    color: 'success' | 'warning' | 'gray' | 'gray-blue';
    label: string;
    tooltip: string;
  }
> = {
  subscribed: {
    color: 'success',
    label: 'Subscribed',
    tooltip: 'Active subscriber in ActiveCampaign',
  },
  unsubscribed: {
    color: 'gray',
    label: 'Unsubscribed',
    tooltip: 'Opted out of ActiveCampaign emails',
  },
  bounced: {
    color: 'warning',
    label: 'Bounced',
    tooltip: 'Bounced in ActiveCampaign',
  },
  synced: {
    color: 'gray-blue',
    label: 'Synced',
    tooltip: 'Synced with ActiveCampaign',
  },
};

export const ActiveCampaignStatusBadge = ({
  status,
}: ActiveCampaignStatusBadgeProps) => {
  if (status == null || !(status in STATUS_DISPLAY)) {
    return null;
  }

  const display = STATUS_DISPLAY[status];

  return (
    <Tooltip title={display.tooltip}>
      <TooltipTrigger>
        <Badge color={display.color} size='sm' type='pill-color'>
          {display.label}
        </Badge>
      </TooltipTrigger>
    </Tooltip>
  );
};
